import React, { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import Avatar from '@mui/material/Avatar'
import { LockOutlined as LockOutlinedIcon } from '@mui/icons-material'
import { formatDeviseErrors } from './deviseErrors'
import { deviseFetch, failureMessage } from './deviseRequest'
import {
  setFlashNotice,
  setFlashAlert,
} from '../../redux/features/ui/uiSlice'

// The page the unlock email links to (/app/user/unlock?unlock_token=...). It
// unlocks the account as soon as it opens (GET /user/unlock.json), then goes
// to sign in, or back to the resend unlock screen if the link doesn't work.
export default function ConfirmUnlock() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const unlockAttempted = useRef(false)

  useEffect(() => {
    // The effect runs twice in development; a token can only be used once.
    if (unlockAttempted.current) {
      return
    }
    unlockAttempted.current = true

    const failed = (message) => {
      dispatch(setFlashAlert(message))
      navigate('/app/resend_unlock')
    }

    const unlockAccount = async () => {
      const unlockToken = new URLSearchParams(window.location.search)
        .get('unlock_token')

      if (!unlockToken) {
        failed('The unlock link was incomplete. Request a new one.')
        return
      }

      const result = await deviseFetch(
        dispatch,
        `/user/unlock.json?unlock_token=${encodeURIComponent(unlockToken)}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      })

      if (result.failure) {
        failed(failureMessage(result))
        return
      }
      const { response, json: json_data } = result

      if (response.status === 200) {
        dispatch(setFlashNotice('Your account has been unlocked. You can now sign in.'))
        navigate('/app/sign_in')
        return
      }

      if (response.status === 422) {
        // A used or expired link can't be fixed here: ask for a new one.
        failed(
          json_data?.errors?.unlock_token
            ? 'The unlock link was invalid or has already been used. Request a new one.'
            : formatDeviseErrors(json_data)
        )
        return
      }

      failed(formatDeviseErrors(json_data))
    }

    unlockAccount().catch((error) => {
      console.error(error)
      failed(formatDeviseErrors(null))
    })
  }, [])

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          marginTop: 9,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
          <LockOutlinedIcon />
        </Avatar>

        <Typography component='h1' variant='h5'>
          Unlocking your account
        </Typography>
      </Box>
    </Container>
  )
}
